import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableIndexCell, TableIndexHead } from '@/components/ui/table-index';
import { rowSerial } from '@/lib/row-serial';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageLoading } from '@/components/ui/loading';
import { Pagination } from '@/components/ui/pagination';
import { getStatusBadgeClass, getStatusLabel, formatDate } from '@/lib/utils';
import { Plus, FileText, Pencil, Trash2, X } from 'lucide-react';
import { ROLES } from '@/lib/permissions';
import { getDashboardMetric } from '@shared/dashboard-metrics';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from '@/components/ui/alert-dialog';
import type { LabourRequirement, Project } from '@/lib/types';

const STATUSES = ['DRAFT', 'PENDING_MANAGER_APPROVAL', 'MANAGER_APPROVED', 'REJECTED_BY_MANAGER', 'APPROVED', 'REJECTED_BY_CLUSTER_HEAD'];
const PAGE_SIZE = 10;

export default function RequirementsListPage() {
  const { hasRole } = useAuth();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Initialize filters from URL params so dashboard drill-downs land on a
  // page that actually shows the filtered data.
  const metricKey = searchParams.get('metric') || '';
  const initialMetric = getDashboardMetric(metricKey) || undefined;
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [projectFilter, setProjectFilter] = useState(searchParams.get('projectId') || 'all');
  const [dateFrom, setDateFrom] = useState(searchParams.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('dateTo') || '');
  const [metric, setMetric] = useState(initialMetric);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1') || 1);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (recordId: string) => api.delete(`/requirements/${recordId}`),
    onSuccess: () => {
      setDeleteTargetId(null);
      addToast('Draft deleted successfully.', 'success');
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err: any) => {
      setDeleteTargetId(null);
      addToast(err.message || 'Failed to delete', 'error');
    },
  });

  // DRAFT is only meaningful for the creator (Engineer) and Admins. Manager,
  // Cluster Head and Viewer are never given drafts, so the option is omitted.
  const canSeeDrafts =
    hasRole(ROLES.SITE_ENGINEER) || hasRole(ROLES.ORG_ADMIN) || hasRole(ROLES.SUPER_ADMIN);
  const statusOptions = STATUSES.filter(s => canSeeDrafts || s !== 'DRAFT');

  // Admins (tenant org admin + platform SUPER_ADMIN) keep full org-wide access
  // and are never pinned to an assigned project.
  const isFullAccess = hasRole(ROLES.ORG_ADMIN) || hasRole(ROLES.SUPER_ADMIN);

  const validDateRange = !dateFrom || !dateTo || dateFrom <= dateTo;

  // Any manual filter change overrides the dashboard snapshot view.
  const applyManualChange = (fn: (v: string) => void) => (value: string) => {
    fn(value);
    setMetric(undefined);
    setPage(1);
  };

  const clearMetric = () => {
    setMetric(undefined);
    setStatusFilter('all');
    setPage(1);
  };

  const { data: requirements, isLoading: reqLoading } = useQuery({
    queryKey: ['requirements', statusFilter, projectFilter, dateFrom, dateTo, metric?.key, page],
    queryFn: async () => {
      if (!validDateRange) return { items: [] as LabourRequirement[], total: 0 };
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (projectFilter !== 'all') params.set('projectId', projectFilter);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (metric) params.set('metric', metric.key);
      params.set('page', String(page));
      params.set('limit', String(PAGE_SIZE));
      const res = await api.get<{ success: boolean; data: LabourRequirement[]; pagination?: { total: number } }>(`/requirements?${params.toString()}`);
      return { items: res.data ?? [], total: res.pagination?.total ?? (res.data ?? []).length };
    },
  });

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects-list'],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: Project[] }>('/projects');
      return (res.data ?? []) as Project[];
    },
  });

  // For scoped users the /projects endpoint already returns only assigned
  // projects. When exactly one is assigned, pin the filter to it so the list is
  // never confused by the "All Projects" option (a URL projectId always wins).
  const autoSelectRef = useRef(true);
  useEffect(() => {
    if (!autoSelectRef.current) return;
    if (!projects) return;
    autoSelectRef.current = false;
    if (isFullAccess) return;
    if (searchParams.get('projectId')) return;
    if (projects.length === 1) setProjectFilter(projects[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, isFullAccess]);

  if (reqLoading) return <PageLoading />;

  // Scoped users with no assignments: the record list is empty by design, so
  // surface a clear state instead of an empty table behind the filters.
  if (!projectsLoading && !isFullAccess && (projects ?? []).length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-heading font-bold text-primary break-words">Departmental Labour-Requirement</h1>
            <p className="text-secondary mt-1 break-words">Manage Departmental Labour-Requirement records across projects</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-10 text-center">
            <p className="text-secondary">No projects assigned. Contact your administrator to get project access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-heading font-bold text-primary break-words">Departmental Labour-Requirement</h1>
          <p className="text-secondary mt-1 break-words">Manage Departmental Labour-Requirement records across projects</p>
        </div>
        {hasRole(ROLES.SITE_ENGINEER) && (
          <Link to="/requirements/new" className="shrink-0">
            <Button className="gap-2 min-h-[44px] w-full sm:w-auto"><Plus size={16} /> New Departmental Labour-Requirement</Button>
          </Link>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid cw-grid-12 gap-3">
            <div className="cw-span-3">
              <Select value={statusFilter} onValueChange={applyManualChange(setStatusFilter)}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {statusOptions.map(s => <SelectItem key={s} value={s}>{getStatusLabel(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="cw-span-5">
              <Select value={projectFilter} onValueChange={applyManualChange(setProjectFilter)}>
                <SelectTrigger><SelectValue placeholder="Project" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {(projects ?? []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="cw-span-2">
              <DatePicker value={dateFrom} onChange={e => { setDateFrom(e.target.value); setMetric(undefined); setPage(1); }} aria-label="Start date" />
            </div>
            <div className="cw-span-2">
              <DatePicker value={dateTo} onChange={e => { setDateTo(e.target.value); setMetric(undefined); setPage(1); }} aria-label="End date" />
            </div>
          </div>
          {!validDateRange && (
            <p className="text-error text-sm mt-2">Start date cannot be after end date.</p>
          )}
        </CardContent>
      </Card>

      {metric && (
        <div className="flex items-center gap-2 px-1">
          <Badge variant="info">Filtered: {metric.label}</Badge>
          <Button variant="ghost" size="sm" onClick={clearMetric} aria-label="Clear dashboard filter">
            <X size={14} className="mr-1" /> Clear filter
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableIndexHead />
                  <TableHead>Project</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requirements && requirements.items.length > 0 ? (
                  requirements.items.map((req, index) => (
                    <TableRow key={req.id}>
                      <TableIndexCell value={rowSerial(index, page, PAGE_SIZE)} />
                      <TableCell className="font-medium">
                        {req.projectName ?? 'Unknown Project'}
                      </TableCell>
                      <TableCell>{formatDate(req.startDate)}</TableCell>
                      <TableCell>{formatDate(req.endDate)}</TableCell>
                      <TableCell><Badge variant={getStatusBadgeClass(req.status) as any}>{getStatusLabel(req.status)}</Badge></TableCell>
                      <TableCell className="text-secondary text-sm">{formatDate(req.createdAt)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Link to={`/requirements/${req.id}`}>
                            <Button variant="ghost" size="icon" title="View Departmental Labour-Requirement"><FileText size={16} /></Button>
                          </Link>
                          {req.canEdit && (
                            <Link to={`/requirements/${req.id}/edit`}>
                              <Button variant="ghost" size="icon" title="Edit Departmental Labour-Requirement"><Pencil size={16} /></Button>
                            </Link>
                          )}
                          {req.canDelete && (
                            <Button variant="ghost" size="icon" title="Delete draft" onClick={() => setDeleteTargetId(req.id)} disabled={deleteMutation.isPending}>
                              <Trash2 size={16} className="text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted">No Departmental Labour-Requirement records found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="md:hidden space-y-3 p-4">
            {requirements && requirements.items.length > 0 ? (
              requirements.items.map(req => (
                <div key={req.id} className="rounded-lg border border-subtle bg-surface p-4 space-y-2 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-primary break-words">{req.projectName ?? 'Unknown Project'}</p>
                    <Badge variant={getStatusBadgeClass(req.status) as any}>{getStatusLabel(req.status)}</Badge>
                  </div>
                  <dl className="text-sm space-y-1">
                    <div className="flex justify-between gap-3">
                      <dt className="text-secondary shrink-0">Start Date</dt>
                      <dd className="text-primary text-right break-words">{formatDate(req.startDate)}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-secondary shrink-0">End Date</dt>
                      <dd className="text-primary text-right break-words">{formatDate(req.endDate)}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-secondary shrink-0">Created</dt>
                      <dd className="text-primary text-right break-words">{formatDate(req.createdAt)}</dd>
                    </div>
                  </dl>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Link to={`/requirements/${req.id}`}>
                      <Button variant="ghost" size="sm" className="min-h-[44px]" title="View Departmental Labour-Requirement"><FileText size={16} className="mr-1" />View</Button>
                    </Link>
                    {req.canEdit && (
                      <Link to={`/requirements/${req.id}/edit`}>
                        <Button variant="ghost" size="sm" className="min-h-[44px]" title="Edit Departmental Labour-Requirement"><Pencil size={16} className="mr-1" />Edit</Button>
                      </Link>
                    )}
                    {req.canDelete && (
                      <Button variant="ghost" size="sm" className="min-h-[44px]" title="Delete draft" onClick={() => setDeleteTargetId(req.id)} disabled={deleteMutation.isPending}>
                        <Trash2 size={16} className="mr-1 text-destructive" />Delete
                      </Button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center py-8 text-muted">No Departmental Labour-Requirement records found</p>
            )}
          </div>
          <Pagination page={page} pageSize={PAGE_SIZE} total={requirements?.total ?? 0} onPageChange={setPage} />
        </CardContent>
      </Card>

      <AlertDialog open={deleteTargetId !== null} onOpenChange={(open) => { if (!open && !deleteMutation.isPending) setDeleteTargetId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete draft?</AlertDialogTitle>
            <AlertDialogDescription>This Departmental Labour-Requirement draft will be permanently deleted. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:pointer-events-none disabled:opacity-50"
              disabled={deleteMutation.isPending}
              onClick={() => { if (deleteTargetId) deleteMutation.mutate(deleteTargetId); }}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Draft'}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
